import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Field from "@galaxy-io/dls/inputs/Field";
import ToggleInput from "@galaxy-io/dls/inputs/ToggleInput";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import Widget, { WidgetSize } from "@galaxy-io/dls/widget/Widget";
import type { UserPreferences } from "@gtm/contracts/core/preferences";
import { SlidersHorizontalIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useRouter } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { SettingsActions } from "./settings-header";

export function GeneralPreferences({ preferences }: { preferences: UserPreferences }) {
  const [saved, setSaved] = useState(preferences);
  const [view, setView] = useState(preferences.signalsView);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const router = useRouter();
  async function save() {
    setBusy(true);
    try {
      const next = await unwrap(
        apiClient().PATCH("/profile/preferences", {
          body: { signalsView: view },
        }),
      );
      setSaved(next);
      setView(next.signalsView);
      router.refresh();
      toast({
        variant: ToastVariant.SUCCESS,
        header: "Preferences saved",
        description: "Applied across your workspaces.",
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } catch (e) {
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not save preferences",
        description: (e as Error).message,
        ...toastDefaults(ToastVariant.ERROR),
      });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Widget
      header="Preferences"
      subheader="Your defaults across workspaces."
      icon={SlidersHorizontalIcon}
      size={WidgetSize.LARGE}
      footer={
        <SettingsActions>
          <Button
            label="Save preferences"
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.SMALL}
            isLoading={busy}
            isDisabled={busy || view === saved.signalsView}
            onClick={() => void save()}
          />
          {view !== saved.signalsView && (
            <Button
              label="Cancel"
              variant={ButtonVariant.TERTIARY}
              size={ButtonSize.SMALL}
              isDisabled={busy}
              onClick={() => setView(saved.signalsView)}
            />
          )}
        </SettingsActions>
      }
    >
      <Field label="Default signals view" description="The view you land on when you open Signals.">
        <ToggleInput
          ariaLabel="Default signals view"
          value={view}
          isDisabled={busy}
          options={[
            { id: "board", label: "Board" },
            { id: "table", label: "Table" },
            { id: "swipe", label: "Swipe" },
          ]}
          onChange={(value) =>
            setView(value === "swipe" ? "swipe" : value === "table" ? "table" : "board")
          }
        />
      </Field>
    </Widget>
  );
}
